import { ProcessedAuction } from '~/App/shared/interfaces/ProcessedAuction';
import { AuctionType } from '~/App/shared/interfaces/Auction';
import { VehicleType } from '~/App/shared/interfaces/ProcessObject';

/**
 * Criteria for tagging a vehicle as eligible for the used electric-vehicle
 * subsidy / EV grant ("elbilspremie"), see KVDBIL-8517.
 *
 * A vehicle is eligible when it is a pure electric passenger car whose price is
 * within the eligible range below.
 */
export const EV_GRANT_MIN_PRICE = 64_800;
export const EV_GRANT_MAX_PRICE = 450_000;

/**
 * The "price" used to evaluate EV grant eligibility:
 * - Fixed-price only (`BUY_NOW`): the current fixed price (buy-now amount).
 * - Auction (`BIDDING`), including auctions that also offer buy-now: the current
 *   leading bid, or the starting bid when no bid has been placed yet.
 */
export const getEvGrantPrice = (auction: ProcessedAuction): number | null => {
  if (auction?.auctionType === AuctionType.BUY_NOW) {
    return auction.buyNowAmount ?? null;
  }

  const leadingBidAmount = auction?.leadingBid?.amount ?? 0;
  if (leadingBidAmount > 0) {
    return leadingBidAmount;
  }

  return auction?.startBid ?? null;
};

export const isEligibleForEvGrant = (auction: ProcessedAuction): boolean => {
  const processObject = auction?.processObject;

  if (!processObject) {
    return false;
  }

  // Must be a pure electric vehicle — hybrids are not eligible.
  if (processObject.properties?.electricType !== 'Electric') {
    return false;
  }

  // Must be registered and approved as a passenger car.
  if (processObject.vehicleType !== VehicleType.Car) {
    return false;
  }

  const price = getEvGrantPrice(auction);
  if (price === null) {
    return false;
  }

  return price >= EV_GRANT_MIN_PRICE && price <= EV_GRANT_MAX_PRICE;
};
